import { useState, useEffect } from 'react';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import React from 'react';

interface UseFirebaseImageReturn {
    src: string;
    isLoading: boolean;
    hasError: boolean;
}

interface CachedUrl {
    url: string;
    cacheDate: string;
}

// Cache for Firebase URLs to avoid re-fetching
const urlCache = new Map<string, CachedUrl>();

const getCurrentDateString = (): string => {
    return new Date().toISOString().split('T')[0];
};

const clearExpiredCache = (): void => {
    const today = getCurrentDateString();
    const expiredKeys: string[] = [];

    urlCache.forEach((cached, key) => {
        if (cached.cacheDate !== today) {
            expiredKeys.push(key);
        }
    });

    expiredKeys.forEach(key => urlCache.delete(key));
};

/**
 * Get the alternative thumbnail path format.
 *
 * There are two thumbnail naming conventions in use:
 * 1. Expected format (iOS/web): `path.jpg_600x600` (suffix after extension)
 * 2. Legacy format (old Cloud Functions): `path_600x600.jpg` (suffix before extension)
 *
 * This function converts between them to enable fallback loading.
 */
const getAlternativeThumbnailPath = (storagePath: string): string | null => {
    // Check if this is a thumbnail/scaled path
    const suffixMatch = storagePath.match(/(_\d+x\d+|_scaled_\d+)$/);
    if (suffixMatch) {
        // Current format: path.jpg_600x600 -> try legacy: path_600x600.jpg
        const suffix = suffixMatch[1];
        const basePath = storagePath.slice(0, -suffix.length);
        const extMatch = basePath.match(/(\.[^.]+)$/);
        if (extMatch) {
            const ext = extMatch[1];
            const pathWithoutExt = basePath.slice(0, -ext.length);
            return `${pathWithoutExt}${suffix}${ext}`;
        }
    }

    // Check for legacy format: path_600x600.jpg
    const legacyMatch = storagePath.match(/^(.+)(_\d+x\d+|_scaled_\d+)(\.[^.]+)$/);
    if (legacyMatch) {
        // Legacy format: path_600x600.jpg -> try expected: path.jpg_600x600
        const [, pathWithoutExt, suffix, ext] = legacyMatch;
        return `${pathWithoutExt}${ext}${suffix}`;
    }

    return null;
};

const getFreshDownloadURL = async (storagePath: string): Promise<string> => {
    const storage = getStorage();
    const imageRef = ref(storage, storagePath);
    return await getDownloadURL(imageRef);
};

/**
 * Try to get download URL, falling back to alternative thumbnail path if primary fails.
 */
export const getFreshDownloadURLWithFallback = async (storagePath: string): Promise<string> => {
    const storage = getStorage();

    try {
        // Try the primary path first
        const imageRef = ref(storage, storagePath);
        return await getDownloadURL(imageRef);
    } catch (primaryError) {
        // If primary fails, try alternative thumbnail path format
        const altPath = getAlternativeThumbnailPath(storagePath);
        if (altPath) {
            try {
                const altRef = ref(storage, altPath);
                const url = await getDownloadURL(altRef);
                console.log(`[ImageLoader] Loaded from alternative path: ${altPath} (primary was: ${storagePath})`);
                return url;
            } catch {
                // Alternative also failed, throw original error
            }
        }
        throw primaryError;
    }
};

/**
 * Imperatively resolve a storage path to a real download URL, using the shared
 * cache when possible. Unlike `useFirebaseImage`, this never returns a
 * placeholder — it resolves the actual URL (or rejects). Use this when building
 * a FancyBox gallery so slides never open with an unresolved placeholder that
 * FancyBox then caches.
 */
export const resolveFirebaseImageUrl = async (storagePath: string): Promise<string> => {
    const today = getCurrentDateString();
    clearExpiredCache();

    const cached = urlCache.get(storagePath);
    if (cached && cached.cacheDate === today) {
        return cached.url;
    }

    const url = await getFreshDownloadURLWithFallback(storagePath);
    urlCache.set(storagePath, { url, cacheDate: today });
    return url;
};

const retryImageLoad = async (
    storagePath: string, 
    today: string, 
    maxRetries: number,
    setSrc: (src: string) => void,
    setIsLoading: (loading: boolean) => void,
    setHasError: (error: boolean) => void
): Promise<void> => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            // Wait before retrying (except for first attempt)
            if (attempt > 1) {
                const delay = 2000; // 2 seconds between each attempt
                await new Promise(resolve => setTimeout(resolve, delay));
            }

            // Clear the potentially revoked URL from cache
            urlCache.delete(storagePath);

            // Get a fresh URL
            const freshUrl = await getFreshDownloadURLWithFallback(storagePath);

            // Cache the new URL
            urlCache.set(storagePath, {
                url: freshUrl,
                cacheDate: today
            });

            // Try loading the fresh URL
            const retryImg = new Image();

            const loadPromise = new Promise<void>((resolve, reject) => {
                retryImg.onload = () => {
                    setSrc(freshUrl);
                    setIsLoading(false);
                    resolve();
                };

                retryImg.onerror = (error) => {
                    console.warn(`Image load failed on attempt ${attempt} for ${storagePath}:`, error);
                    reject(new Error(`Image load failed on attempt ${attempt}`));
                };
            });

            retryImg.src = freshUrl;
            await loadPromise;
            
            // If we get here, the image loaded successfully
            return;

        } catch (retryError) {
            console.warn(`Retry attempt ${attempt} failed for ${storagePath}:`, retryError);
            
            if (attempt === maxRetries) {
                console.error(`All ${maxRetries} retry attempts failed for ${storagePath}`);
                setHasError(true);
                setIsLoading(false);
            }
        }
    }
};

export const useFirebaseImage = (
    storagePath: string | null | undefined,
    placeholderSrc: string = '/Images/placeholder.png'
): UseFirebaseImageReturn => {
    const [src, setSrc] = useState<string>(placeholderSrc);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [hasError, setHasError] = useState<boolean>(false);

    useEffect(() => {
        if (!storagePath) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setHasError(false);
        setSrc(placeholderSrc);

        const loadFirebaseImage = async () => {
            try {
                let imageUrl: string;
                const today = getCurrentDateString();

                // Clear expired cache entries
                clearExpiredCache();

                // Check if we have a cached URL for today
                const cached = urlCache.get(storagePath);

                if (cached && cached.cacheDate === today) {
                    imageUrl = cached.url;
                } else {
                    // Get fresh download URL
                    imageUrl = await getFreshDownloadURLWithFallback(storagePath);

                    // Cache with today's date
                    urlCache.set(storagePath, {
                        url: imageUrl,
                        cacheDate: today
                    });
                }

                // Preload the image with revocation handling
                const img = new Image();

                img.onload = () => {
                    setSrc(imageUrl);
                    setIsLoading(false);
                };

                img.onerror = (error) => {
                    console.warn(`Image load failed for ${storagePath}, attempting retries...`);
                    retryImageLoad(storagePath, today, 10, setSrc, setIsLoading, setHasError).catch(err => {
                        console.error('Retry logic failed:', err);
                    });
                };

                img.src = imageUrl;

                return () => {
                    img.onload = null;
                    img.onerror = null;
                };
            } catch (error) {
                console.warn(`Failed to get download URL for ${storagePath}, attempting retries...`, error);
                // If we get a 404 or other error getting the download URL, retry
                retryImageLoad(storagePath, getCurrentDateString(), 10, setSrc, setIsLoading, setHasError).catch(err => {
                    console.error('Retry logic failed:', err);
                    setHasError(true);
                    setIsLoading(false);
                });
            }
        };

        const cleanup = loadFirebaseImage();

        return () => {
            cleanup?.then(cleanupFn => cleanupFn?.());
        };
    }, [storagePath, placeholderSrc]);

    return { src, isLoading, hasError };
};

export enum ImageSize {
    FULL = 'FULL',
    THUMBNAIL_MEDIUM = 'THUMBNAIL_MEDIUM',  // Cropped square 600x600
    THUMBNAIL_SMALL = 'THUMBNAIL_SMALL',    // Cropped square 200x200
    SCALED_MEDIUM = 'SCALED_MEDIUM',        // Aspect-ratio preserving, max 600px
    SCALED_SMALL = 'SCALED_SMALL'           // Aspect-ratio preserving, max 200px
}

export const getImagePathWithSize = (basePath: string | null | undefined, size: ImageSize): string | null => {
    if (!basePath) return null;

    switch (size) {
        case ImageSize.FULL:
            return basePath;
        case ImageSize.THUMBNAIL_MEDIUM:
            return `${basePath}_600x600`;
        case ImageSize.THUMBNAIL_SMALL:
            return `${basePath}_200x200`;
        case ImageSize.SCALED_MEDIUM:
            return `${basePath}_scaled_600`;
        case ImageSize.SCALED_SMALL:
            return `${basePath}_scaled_200`;
        default:
            return basePath;
    }
};

interface FirebaseImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    storagePath: string | null | undefined;
    size: ImageSize;
    placeholder?: string;
}

export const FirebaseImage: React.FC<FirebaseImageProps> = ({
                                                                storagePath,
                                                                size = ImageSize.FULL,
                                                                placeholder = undefined,
                                                                alt = '',
                                                                className = '',
                                                                ...props
                                                            }) => {
    const processedPath = getImagePathWithSize(storagePath, size);
    const { src, isLoading, hasError } = useFirebaseImage(processedPath, placeholder);

    return (
        <img
            src={src}
            alt={alt}
            className={`${className} ${isLoading ? 'loading' : ''} ${hasError ? 'error' : ''}`}
            {...props}
        />
    );
};